import { plusTwelveMonths } from '../../utils/engagementPageModel';

/** EQ-006 (D-221 §4): a purchase whose hours moved to a newer purchase is closed - nothing to extend (UI only; the DB status is untouched). */
export function canExtendBank(b: { transferred_to_bank_id?: string | null }): boolean {
  return !b.transferred_to_bank_id;
}

/**
 * BQ-063 (D-31): the "new purchase" dialog's dates. The purchase date defaults to today and the
 * expiry to the purchase date + 12 months; changing the purchase date moves the expiry along
 * until the user edits the expiry by hand.
 */
export interface PurchaseDates { purchased_at: string; expiry_date: string; expiryTouched: boolean }

export function initialPurchaseDates(today: string): PurchaseDates {
  return { purchased_at: today, expiry_date: plusTwelveMonths(today), expiryTouched: false };
}

export function nextPurchaseDates(prev: PurchaseDates, key: 'purchased_at' | 'expiry_date', value: string): PurchaseDates {
  if (key === 'expiry_date') return { ...prev, expiry_date: value, expiryTouched: true };
  return {
    ...prev,
    purchased_at: value,
    expiry_date: prev.expiryTouched || !value ? prev.expiry_date : plusTwelveMonths(value),
  };
}

/** BQ-064 (QX-1287): the dialog's "more recipients" text -> addresses (comma / semicolon / space separated). */
export function parseExtraRecipients(text: string): { emails: string[]; invalid: string[] } {
  const emails: string[] = [];
  const invalid: string[] = [];
  for (const part of text.split(/[,;\s]+/)) {
    const e = part.trim();
    if (!e) continue;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) invalid.push(e);
    else if (!emails.some((x) => x.toLowerCase() === e.toLowerCase())) emails.push(e);
  }
  return { emails, invalid };
}

/**
 * D-272 (D-221, QX-1287): a customer with an active hour bank gets no second bank. "+ new hour bank"
 * opens the existing bank's "new purchase" dialog instead (the leftover hours move to the new
 * purchase, D-221). "Active" is decided by the SERVER (GET /engagements/active-hour-bank, the same
 * function as the 409 HOUR_BANK_ALREADY_ACTIVE guard, which stays): expired / finished / closed is not active.
 * Returns the path to open, or null when a new bank may be opened.
 */
export async function newBankRedirect(
  lookup: () => Promise<{ data?: { engagement_id?: string } | null } | null | undefined>,
): Promise<string | null> {
  try {
    const id = (await lookup())?.data?.engagement_id;
    return id ? newPurchasePath(id) : null;
  } catch {
    return null; // lookup failed: open the form; the server 409 guard still protects
  }
}

export const newPurchasePath = (engagementId: string): string => `/engagements/${engagementId}?tab=bank&newPurchase=1`;
